import React, { useState } from 'react';
import Modal from '../../../components/ui/Modal';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';
import Icon from '../../../components/AppIcon';

const ExportAlertsModal = ({ isOpen, onClose }) => {
  const [exportFormat, setExportFormat] = useState('excel');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [includeResolved, setIncludeResolved] = useState(false);
  const [selectedCategories, setSelectedCategories] = useState([]);

  const handleExport = () => {
    if (!dateFrom || !dateTo) {
      alert('يرجى تحديد نطاق التاريخ');
      return;
    }
    console.log('Exporting alerts:', { exportFormat, dateFrom, dateTo, includeResolved, selectedCategories });
    alert(`تم تصدير التقرير بصيغة ${exportFormat === 'excel' ? 'Excel' : exportFormat === 'pdf' ? 'PDF' : 'CSV'}`);
    onClose();
  };

  const categories = [
    { id: 'client_followup', label: 'متابعة العملاء' },
    { id: 'contract_deadline', label: 'مواعيد العقود' },
    { id: 'property_expiration', label: 'انتهاء العقارات' },
    { id: 'payment_reminder', label: 'تذكير الدفع' },
    { id: 'maintenance_alert', label: 'تنبيهات الصيانة' }
  ];

  const toggleCategory = (categoryId) => {
    setSelectedCategories(prev => 
      prev?.includes(categoryId) 
        ? prev?.filter(id => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="تصدير التقرير"
      icon="Download"
      iconColor="#EC4899"
      size="lg"
      footer={
        <div className="flex gap-3">
          <Button
            onClick={handleExport}
            className="flex-1"
            variant="primary"
          >
            <span className="flex items-center gap-2">
              <Icon name="Download" size={18} />
              تصدير التقرير
            </span>
          </Button>
          
          <Button
            onClick={onClose}
            variant="outline"
          >
            إلغاء
          </Button>
        </div>
      }
    >
      <div className="space-y-6">
        <div>
          <h3 className="text-lg font-semibold text-foreground mb-4">إعدادات التصدير</h3>
          
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">صيغة التصدير</label>
              <div className="grid grid-cols-3 gap-3">
                <button
                  onClick={() => setExportFormat('excel')}
                  className={`p-4 border rounded-lg text-center transition-colors ${
                    exportFormat === 'excel' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted/30'
                  }`}
                >
                  <Icon name="FileSpreadsheet" size={24} className="mx-auto mb-2" />
                  <p className="text-sm font-medium">Excel</p>
                </button>
                
                <button
                  onClick={() => setExportFormat('pdf')}
                  className={`p-4 border rounded-lg text-center transition-colors ${
                    exportFormat === 'pdf' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted/30'
                  }`}
                >
                  <Icon name="FileText" size={24} className="mx-auto mb-2" />
                  <p className="text-sm font-medium">PDF</p>
                </button>
                
                <button
                  onClick={() => setExportFormat('csv')}
                  className={`p-4 border rounded-lg text-center transition-colors ${
                    exportFormat === 'csv' ? 'border-primary bg-primary/10 text-primary' : 'border-border hover:bg-muted/30'
                  }`}
                >
                  <Icon name="FileCode" size={24} className="mx-auto mb-2" />
                  <p className="text-sm font-medium">CSV</p>
                </button>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">من تاريخ</label>
                <Input
                  type="date"
                  value={dateFrom}
                  onChange={(e) => setDateFrom(e?.target?.value)}
                  className="w-full"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">إلى تاريخ</label>
                <Input
                  type="date"
                  value={dateTo}
                  onChange={(e) => setDateTo(e?.target?.value)}
                  className="w-full"
                />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium mb-3">فئات التنبيهات</label>
              <div className="space-y-2">
                {categories?.map(category => (
                  <label key={category?.id} className="flex items-center gap-2 p-2 hover:bg-muted/30 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={selectedCategories?.includes(category?.id)}
                      onChange={() => toggleCategory(category?.id)}
                      className="w-4 h-4"
                    />
                    <span className="text-sm">{category?.label}</span>
                  </label>
                ))}
              </div>
            </div>

            <div className="flex items-center gap-3 p-4 border border-border rounded-lg">
              <input
                type="checkbox"
                id="include-resolved"
                checked={includeResolved}
                onChange={(e) => setIncludeResolved(e?.target?.checked)}
                className="w-4 h-4"
              />
              <label htmlFor="include-resolved" className="flex-1">
                <span className="font-medium">تضمين التنبيهات المحلولة</span>
                <p className="text-sm text-muted-foreground">إضافة التنبيهات التي تم حلها إلى التقرير</p>
              </label>
            </div>
          </div>
        </div>

        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex items-start gap-2">
            <Icon name="Info" size={20} className="text-blue-600 mt-1" />
            <div>
              <h4 className="font-semibold text-blue-900 mb-1">معلومات التصدير</h4>
              <ul className="text-sm text-blue-800 space-y-1">
                <li>• سيتم تضمين جميع التفاصيل والتعليقات</li>
                <li>• التقرير يحتوي على إحصائيات شاملة</li>
                <li>• يمكن مشاركة التقرير مع الإدارة</li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
};

export default ExportAlertsModal;